import { Button } from '../../components/Button';
import { PriceDisplay } from '../../components/PriceDisplay';
import { BasketContents } from '../../composites/BasketContents';
import { DiscountCode } from '../../composites/DiscountCode';
import {
  BasketSection,
  DiscountColumn,
  DiscountRow,
  HeaderActions,
  HeaderActionsButton,
  HeaderBlock,
  PageWrapper,
  TotalHeading,
  TotalHeadingPrefix,
} from './BasketTemplate.styles';
import type { BasketTemplateProps } from './BasketTemplate.types';

export function BasketTemplate({
  items,
  totalPrice,
  view = 'itemised',
  onViewChange,
  guests,
  onRemoveItem,
  promoCode,
  voucherCode,
  subtotal,
  promotionsDiscount,
  vouchersDiscount,
  onContinueShopping,
  onCheckout,
  showAddedConfirmation = false,
  locale,
  labels,
}: BasketTemplateProps) {
  const {
    totalHeadingPrefix,
    continueShopping,
    checkout,
    basketHeading,
    itemisedView,
    itineraryView,
    addedConfirmation,
    subtotal: subtotalLabel,
    promotions,
    vouchers,
    total,
    removeLabel,
    noImageLabel,
    selectGuestLabel,
  } = labels;

  const showDiscountRow = Boolean(promoCode || voucherCode);

  const discountSlot = showDiscountRow ? (
    <DiscountRow>
      {promoCode && (
        <DiscountColumn>
          <DiscountCode {...promoCode} />
        </DiscountColumn>
      )}
      {voucherCode && (
        <DiscountColumn>
          <DiscountCode {...voucherCode} />
        </DiscountColumn>
      )}
    </DiscountRow>
  ) : undefined;

  return (
    <PageWrapper data-testid="basket-template">
      <HeaderBlock>
        <TotalHeading>
          <TotalHeadingPrefix>{totalHeadingPrefix}</TotalHeadingPrefix>
          <PriceDisplay
            amount={totalPrice.amount}
            currencyCode={totalPrice.currencyCode}
            locale={locale}
            size="xlarge"
            weight="medium"
          />
        </TotalHeading>
        {(onContinueShopping || onCheckout) && (
          <HeaderActions>
            {onContinueShopping && (
              <HeaderActionsButton>
                <Button variant="secondary" fullWidth onClick={onContinueShopping}>
                  {continueShopping}
                </Button>
              </HeaderActionsButton>
            )}
            {onCheckout && (
              <HeaderActionsButton>
                <Button variant="primary" fullWidth onClick={onCheckout}>
                  {checkout}
                </Button>
              </HeaderActionsButton>
            )}
          </HeaderActions>
        )}
      </HeaderBlock>

      <BasketSection>
        <BasketContents
          items={items}
          totalPrice={totalPrice}
          view={view}
          onViewChange={onViewChange}
          guests={guests}
          showAddedConfirmation={showAddedConfirmation}
          onRemoveItem={onRemoveItem}
          discountSlot={discountSlot}
          subtotal={subtotal}
          promotionsDiscount={promotionsDiscount}
          vouchersDiscount={vouchersDiscount}
          locale={locale}
          labels={{
            title: basketHeading,
            itemisedView,
            itineraryView,
            addedConfirmation,
            subtotal: subtotalLabel,
            promotions,
            vouchers,
            total,
            removeLabel,
            noImageLabel,
            selectGuestLabel,
          }}
        />
      </BasketSection>
    </PageWrapper>
  );
}
